import { useEffect, useRef, useState } from 'react'

import { Timeline } from '@mui/icons-material'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Skeleton,
  Alert,
} from '@mui/material'

import { handleApiError, type EventLog } from '../../api'

import { EventLogTable, type EventLogTableColumn } from './event-log-table'

type ActivityLogCardProps = {
  title: string
  emptyMessage: string
  columns: ReadonlyArray<EventLogTableColumn>
  fetchEvents: () => Promise<EventLog[]>
}

export function ActivityLogCard({ title, emptyMessage, columns, fetchEvents }: ActivityLogCardProps) {
  const [events, setEvents] = useState<EventLog[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const requestIdRef = useRef(0)

  useEffect(() => {
    const currentRequestId = ++requestIdRef.current

    const load = async () => {
      try {
        setIsLoading(true)
        setError('')
        const fetched = await fetchEvents()
        if (requestIdRef.current !== currentRequestId) return
        setEvents(fetched)
      } catch (err) {
        if (requestIdRef.current !== currentRequestId) return
        setError(handleApiError(err))
      } finally {
        if (requestIdRef.current === currentRequestId) {
          setIsLoading(false)
        }
      }
    }
    load()
  }, [fetchEvents])

  return (
    <Card variant="outlined">
      <CardContent sx={{ p: 0 }}>
        <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 1, borderBottom: 1, borderColor: 'divider' }}>
          <Timeline fontSize="small" color="primary" />
          <Typography variant="subtitle1" fontWeight={600}>
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
            {isLoading ? '...' : `${events.length} events`}
          </Typography>
        </Box>

        {isLoading ? (
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Skeleton variant="rectangular" height={36} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rectangular" height={36} sx={{ borderRadius: 1 }} />
            <Skeleton variant="rectangular" height={36} sx={{ borderRadius: 1 }} />
          </Box>
        ) : error ? (
          <Box sx={{ p: 2 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        ) : events.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <Timeline sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {emptyMessage}
            </Typography>
          </Box>
        ) : (
          <EventLogTable
            events={events}
            columns={columns}
            maxHeight={400}
            stickyHeader
          />
        )}
      </CardContent>
    </Card>
  )
}
